import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api.service';
import { ApiResponse } from '../common/types';
import { LOCALSTORAGE_KEYS } from '../constants/databases';
import { ModalContentTypes } from '../constants/modal-content-types';
import { LocalStorageService } from '../local-storage.service';
import { ModalContentService } from '../modal-content/modal-content.service';
import { RequestHandlerParams } from '../types/api-service.types';
import { Session } from '../types/session.type';
import { MembershipConfirmation, PendingMembershipCharge } from './pending-membership.types';

/**
 * DEV-11022 — asks the backend whether saving a card is about to start a membership
 * the customer picked a while ago, and opens the modal that lets them confirm it or
 * move to a free level first.
 *
 * Every card form goes through here before it tokenizes, so nothing is charged
 * without an explicit choice. The backend is the one enforcing it: without the
 * confirmation the POST answers 409 instead of charging, which is what keeps the
 * brands and clients that never call this endpoint safe.
 */
@Injectable({ providedIn: 'root' })
export class PendingMembershipService {

  #apiService = inject(ApiService);
  #localStorageService = inject(LocalStorageService);
  #modalContentService = inject(ModalContentService);

  #endpoint = '/account/membership/pending-charge';

  /**
   * The check only matters for an account with no card on file — saving one is what
   * triggers the charge. `settings.isAbandoned` is the flag the backend gates on,
   * but it is read from the session written at login, so a signup that reaches the
   * billing step within this same session is not flagged yet; with `N = 0` the
   * backend asks that one to confirm too. The card check covers both, and any
   * account it lets through gets `requiresConfirmation: false` back.
   */
  shouldCheckPendingCharge(): boolean {
    const session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    return !!session?.accountInfo?.token && !session?.settings?.hasPaymentMethod;
  }

  /**
   * Emits the pending charge only when the customer has to confirm it; anything
   * else — the setting off on this brand, a backend that predates the endpoint, a
   * network error — emits `null` or nothing at all and leaves the form as it is.
   * Failing open here is safe because the POST still refuses to charge.
   */
  getPendingCharge(): Observable<PendingMembershipCharge | null> {
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: this.#endpoint,
      apiV3: true,
      showErrorMessage: false
    };

    return this.#apiService.handleRequest<ApiResponse<PendingMembershipCharge>>(params).pipe(
      map(response => response?.data?.requiresConfirmation ? response.data : null)
    );
  }

  /**
   * Opens the confirmation modal. Emits the chosen option, or `null` when the
   * customer backs out — in which case the card must not be sent and the account
   * stays abandoned, exactly as it was.
   */
  openConfirmation(pendingCharge: PendingMembershipCharge): Observable<MembershipConfirmation | null> {
    return this.#modalContentService.openModal(ModalContentTypes.CONFIRM_PENDING_MEMBERSHIP, {
      title: 'Confirm your membership',
      closeable: true,
      fullScreenOnMobile: true,
      pendingMembership: pendingCharge
    }).closed.pipe(
      // The shell's close button emits its own payload, so anything without a fee
      // level is a dismissal.
      map((result: MembershipConfirmation | null) => result?.feeLevelId ? result : null)
    );
  }
}
